import { Award, AlertCircle, ChevronDown, FileText, GraduationCap, Lightbulb, Loader2, Notebook, Sparkles, TrendingUp, User, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { ApiService } from '../../../services/api';

const normalizeLabel = (value: string) => (value || '')
    .toLowerCase()
    .replace(/ı/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u')
    .replace(/ş/g, 's').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, ' ').trim();

const getCompactLabel = (name: string) => {
    const n = normalizeLabel(name);
    if (n === 'turkce') return 'Türkçe';
    if (n === 'sosyal bilimler') return 'Sosyal Bil.';
    if (n === 'temel matematik') return 'Temel Mat.';
    if (n === 'fen bilimleri') return 'Fen Bil.';
    if (n.includes('turk dili ve edebiyati') || n.includes('edebiyati sosyal bilimler 1')) return 'Türk D. ve Edeb.';
    if (n === 'sosyal bilimler 2') return 'Sosyal Bil.-2';
    if (n === 'matematik') return 'Matematik';
    return (name || 'Test').substring(0, 20);
};

const getColumnRank = (name: string, totalQ: number, originalIndex: number) => {
    const n = normalizeLabel(name);
    if (n === 'turkce') return 1;
    if (n === 'sosyal bilimler') return originalIndex >= 4 ? 6 : 2;
    if (n === 'temel matematik') return 3;
    if (n === 'fen bilimleri') return totalQ > 20 ? 8 : 4;
    if (n.includes('turk dili ve edebiyati') || n.includes('edebiyati sosyal bilimler 1')) return 5;
    if (n === 'sosyal bilimler 2') return 6;
    if (n === 'matematik') return 7;
    return 100 + originalIndex;
};

// Akademi sistemi YKS düzeni: 1-4 TYT (Türkçe, Sosyal, Temel Mat., Fen), 5-8 AYT (TDE-Sos.1, Sos.2, Mat., Fen).
// Öğrenci AYT'de yalnız alanının iki testinden sorumludur (ÖSYM puan türleri).
const YKS_ALAN_TESTS: Record<string, { label: string; tests: number[] }> = {
    SAY: { label: 'Sayısal', tests: [7, 8] },
    EA: { label: 'Eşit Ağırlık', tests: [5, 7] },
    SOZ: { label: 'Sözel', tests: [5, 6] },
};
const AYT_TEST_IDS = [5, 6, 7, 8];

// Alan, AYT'de en çok işaretlenen test çiftinden çıkar; eşitlikte ana puan türü. AYT'ye girmeyen yalnız TYT'den sorumludur.
// Bilinen alan (hesaptaki alan ya da yalnız AYT denemesinde puanlamanın alanı) tahmine üstün gelir; dört testi de dolduran SAY'cı
// Edebiyat-Sos1 + Sosyal-2 daha dolu diye SÖZ/EA görünüyordu.
const resolveYksAlan = (answeredByTest: Record<number, number>, preferred?: string | null, fixed?: string | null) => {
    const known = String(fixed || '').trim().toLocaleUpperCase('tr-TR').replace('Ö', 'O');
    if (known && YKS_ALAN_TESTS[known]) return known;
    if (!AYT_TEST_IDS.some((id) => (answeredByTest[id] || 0) > 0)) return null;
    const order = ['SAY', 'EA', 'SOZ'].sort((a, b) => Number(b === preferred) - Number(a === preferred));
    let best: string | null = null;
    let bestScore = -1;
    for (const alan of order) {
        const score = YKS_ALAN_TESTS[alan].tests.reduce((sum, id) => sum + (answeredByTest[id] || 0), 0);
        if (score > bestScore) { best = alan; bestScore = score; }
    }
    return best;
};

// Karne listesindeki yüzde de karnedeki özetle aynı olsun: TYT + alan testlerinin neti / soru sayısı.
export const yksScopedSuccess = (answers: Record<string, any> = {}, preferred?: string | null, fixed?: string | null) => {
    const answered: Record<number, number> = {};
    Object.entries(answers).forEach(([id, t]) => { answered[Number(id)] = Number(t?.correct || 0) + Number(t?.incorrect || 0); });
    const alan = resolveYksAlan(answered, preferred, fixed);
    const alanTests = alan ? YKS_ALAN_TESTS[alan].tests : [];
    let net = 0;
    let totalQ = 0;
    Object.entries(answers).forEach(([id, t]) => {
        if (Number(id) > 4 && !alanTests.includes(Number(id))) return;
        net += Number(t?.correct || 0) - Number(t?.incorrect || 0) * 0.25;
        totalQ += Number(t?.totalQuestions || 0);
    });
    return totalQ > 0 ? (net / totalQ) * 100 : 0;
};

// Yayınevi alan puanlı deneme (alan_puani.js): özet yalnız öğrencinin alanındaki testlerden (testIds). 4 × 30 soruluk 11. sınıf
// denemesinde öğrenci 60 soru çözüyor; 120 üzerinden başarı düşük çıkıyordu (26 Eki).
const ALAN_PUANI_LABELS: Record<string, string> = { SAY: 'Sayısal', EA: 'Eşit Ağırlık', SOZ: 'Sözel' };
export const alanScopedSuccess = (answers: Record<string, any> = {}, testIds: string[] = []) => {
    let net = 0;
    let totalQ = 0;
    Object.entries(answers).forEach(([id, t]) => {
        if (!testIds.includes(String(id))) return;
        net += Number(t?.correct || 0) - Number(t?.incorrect || 0) * 0.25;
        totalQ += Number(t?.totalQuestions || 0);
    });
    return totalQ > 0 ? (net / totalQ) * 100 : 0;
};

const withSession = (name: string, session: string | null) =>
    (session && !normalizeLabel(name).startsWith(session.toLowerCase()) ? `${session} ${name}` : name);

const filterBranchQuestions = (questions: any[], branchSelections: any) =>
    questions.filter((q: any) => {
        const tid = Number(q?.testId || 0);
        const sub = normalizeLabel(q?.subjectName || '');
        if (tid === 2) {
            const sel = branchSelections?.sosyal_bilimler === 'felsefe' ? 'felsefe' : 'din';
            return sel === 'felsefe' ? !sub.includes('din kulturu') : !sub.includes('ilave felsefe');
        }
        if (tid === 6) {
            const sel = branchSelections?.sosyal_bilimler2 === 'felsefe' ? 'felsefe' : 'din';
            return sel === 'felsefe' ? !sub.includes('din kulturu') : !sub.includes('ilave felsefe');
        }
        return true;
    });

const SUBJECT_COLORS: Record<string, { bg: string; bar: string; text: string; light: string }> = {
    turkce: { bg: 'bg-blue-50', bar: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-100' },
    sosyal: { bg: 'bg-amber-50', bar: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-100' },
    matematik: { bg: 'bg-emerald-50', bar: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-100' },
    fen: { bg: 'bg-purple-50', bar: 'bg-purple-500', text: 'text-purple-700', light: 'bg-purple-100' },
    edebiyat: { bg: 'bg-rose-50', bar: 'bg-rose-500', text: 'text-rose-700', light: 'bg-rose-100' },
    default: { bg: 'bg-gray-50', bar: 'bg-gray-500', text: 'text-gray-700', light: 'bg-gray-100' },
};

const getSubjectColor = (name: string) => {
    const n = normalizeLabel(name);
    if (n.includes('turkce') || n.includes('turk dili')) return SUBJECT_COLORS.edebiyat;
    if (n.includes('edebiyat')) return SUBJECT_COLORS.edebiyat;
    if (n.includes('sosyal')) return SUBJECT_COLORS.sosyal;
    if (n.includes('matematik') || n.includes('temel mat')) return SUBJECT_COLORS.matematik;
    if (n.includes('fen')) return SUBJECT_COLORS.fen;
    return SUBJECT_COLORS.default;
};

const cleanAiText = (value: any) => {
    const text = String(value || '').trim();
    return text.replace(/^["']+|["']+$/g, '').trim();
};

const formatRank = (value: any) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric.toLocaleString('tr-TR') : '-';
};

interface ProcessedColumn {
    header: string;
    fullHeader: string;
    testId: string | number;
    session: string | null;
    data: { totalQ: number; correct: number; incorrect: number; empty: number; net: number; avgNet: number; avgSuccessRate: number };
    questions: any[];
    originalIndex: number;
}

const StatCard = ({ label, value, sub, color, large }: { label: string; value: string; sub?: string; color: string; large?: boolean }) => {
    const colorMap: Record<string, string> = {
        blue: 'from-blue-500 to-blue-600',
        green: 'from-emerald-500 to-emerald-600',
        red: 'from-red-500 to-red-600',
        purple: 'from-purple-500 to-purple-600',
        amber: 'from-amber-500 to-amber-600',
    };

    const textColorMap: Record<string, string> = {
        blue: 'text-blue-600',
        green: 'text-emerald-600',
        red: 'text-red-600',
        purple: 'text-purple-600',
        amber: 'text-amber-600',
    };

    return (
        <div className={`relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm ${large ? 'col-span-1' : ''}`}>
            <div className={`absolute top-0 left-0 h-1 w-full bg-gradient-to-r ${colorMap[color] || colorMap.blue}`} />
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
            <p className={`mt-1 text-2xl font-bold ${textColorMap[color] || 'text-gray-800'}`}>{value}</p>
            {sub && <p className="mt-0.5 text-[11px] text-gray-400">{sub}</p>}
        </div>
    );
};

export default function WebStudentReportCard({
    data,
    examName,
    companyLogo,
    companyName,
    totalStudents = 0,
    disableAi = false,
    onAnalysisSaved,
}: {
    data: any;
    examName?: string;
    companyLogo?: string;
    companyName?: string;
    totalStudents?: number;
    // Herkese açık karne (dış okul öğrencisi): AI analizi oturum ister, gösterilmez.
    disableAi?: boolean;
    // Analiz sonuca kaydedilince (ör. karne sekmesi öğrenci listesini güncellesin; başka öğrenciye geçip dönünce kaybolmasın)
    onAnalysisSaved?: (resultId: string, aiAnalysis: { content: any; generatedAt: string }) => void;
}) {
    const { student, info, stats, subjects, bookletType, allQuestions = [] } = data;
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const initialAiResult = data?.additionalInfo?.aiAnalysis?.content || data?.stats?.aiAnalysis?.content || null;
    const [aiResult, setAiResult] = useState<any>(initialAiResult);
    const [isAiSectionOpen, setIsAiSectionOpen] = useState(false);
    // Kayıtlı analizle açılan karne "kayıtlı" sayılır; yeni analizde sunucunun kaydedip kaydetmediği gösterilir
    const [aiSaveState, setAiSaveState] = useState<'saved' | 'unsaved' | null>(initialAiResult ? 'saved' : null);

    useEffect(() => {
        const nextAiResult = data?.additionalInfo?.aiAnalysis?.content || data?.stats?.aiAnalysis?.content || null;
        setAiResult(nextAiResult);
        setAiSaveState(nextAiResult ? 'saved' : null);
        setIsAiSectionOpen(false);
    }, [data?.additionalInfo?.aiAnalysis?.content, data?.stats?.aiAnalysis?.content]);

    const fullName = (student?.firstName && student?.lastName)
        ? `${student.firstName} ${student.lastName}`
        : (info?.fullName || student?.metadata?.parsedName || 'Öğrenci');
    const sube = student?.department?.name || student?.metadata?.className || info?.class || '';
    // LGS'de 3 yanlış 1 doğruyu, YKS'de 4 yanlış 1 doğruyu götürür.
    const netPenalty = data?.examConfig?.levelType === 'lgs' ? 1 / 3 : 0.25;
    // Test numaraları yalnız Akademi sistemi YKS denemesinde sabit düzendedir (yayınevi denemesinde anahtar sırası).
    const isInternalYks = data?.examConfig?.levelType === 'yks'
        && (data?.examConfig?.systemType === 'internal' || Boolean(data?.examConfig?.isInternal));
    const sessionOf = (testId: any) => (isInternalYks ? (Number(testId) <= 4 ? 'TYT' : 'AYT') : null);
    // Yalnız AYT denemesi (11. sınıf alan denemesi): TYT oturumu ve TYT puanı yok, özet yalnız alan testlerinden
    const isSingleAyt = data?.examConfig?.yksMode === 'single_ayt';
    const branchSelections = info?.branchSelections || student?.metadata?.branchSelections || {};
    const filteredQuestions = useMemo(() => filterBranchQuestions(allQuestions, branchSelections), [allQuestions, branchSelections]);

    const filteredQMap = useMemo(() => {
        const map: Record<string, any[]> = {};
        filteredQuestions.forEach((q: any) => {
            const key = String(q?.testId || '');
            if (!map[key]) map[key] = [];
            map[key].push(q);
        });
        return map;
    }, [filteredQuestions]);

    const columns: ProcessedColumn[] = useMemo(() =>
        subjects.map((s: any, index: number) => {
            const questions = [...(filteredQMap[String(s.testId)] || s.questions || [])].sort(
                (a: any, b: any) => Number(a?.questionNumber || 0) - Number(b?.questionNumber || 0)
            );
            const totalQ = questions.length || s.totalQ || 0;
            const correct = questions.filter((q: any) => q.isCorrect).length;
            const incorrect = questions.filter((q: any) => q.isAnswered && !q.isCorrect).length;
            const empty = questions.filter((q: any) => !q.isAnswered).length;
            const session = sessionOf(s.testId);
            return {
                header: withSession(getCompactLabel(s.name || ''), session),
                fullHeader: withSession(s.name || `Test ${index + 1}`, session),
                testId: s.testId,
                session,
                data: {
                    totalQ,
                    correct,
                    incorrect,
                    empty,
                    net: correct - incorrect * netPenalty,
                    avgNet: s.avgNet || 0,
                    avgSuccessRate: s.avgSuccessRate || 0,
                },
                questions,
                originalIndex: index,
            };
        }).sort((a: ProcessedColumn, b: ProcessedColumn) => (isInternalYks
            // kitapçık sırası: TYT 1-4, AYT 5-8
            ? Number(a.testId) - Number(b.testId)
            : getColumnRank(a.fullHeader, a.data.totalQ, a.originalIndex) - getColumnRank(b.fullHeader, b.data.totalQ, b.originalIndex))
        ), [subjects, filteredQMap, netPenalty, isInternalYks]);

    const scoring = stats?.scoring || {};
    const alanPuani = data?.alanPuani?.alan ? data.alanPuani : null;
    const alanTestIds: string[] | null = alanPuani && Array.isArray(alanPuani.testIds) && alanPuani.testIds.length
        ? alanPuani.testIds.map(String)
        : null;

    // YKS: özet yalnız TYT + alanın iki AYT testinden; alan dışında işaretlenen test ayrıca gösterilir, toplama girmez.
    const alan = useMemo(() => {
        if (!isInternalYks) return null;
        const answered: Record<number, number> = {};
        columns.forEach((col) => { answered[Number(col.testId)] = col.data.correct + col.data.incorrect; });
        return resolveYksAlan(answered, scoring?.primaryScore?.scoreType, isSingleAyt ? scoring?.primaryScore?.scoreType : student?.metadata?.alan);
    }, [columns, isInternalYks, isSingleAyt, scoring?.primaryScore?.scoreType, student?.metadata?.alan]);
    const { scopedColumns, outsideColumns, visibleColumns } = useMemo(() => {
        const alanTests = alan ? YKS_ALAN_TESTS[alan].tests : [];
        const scoped = columns.filter((col) => (isInternalYks
            ? col.session === 'TYT' || alanTests.includes(Number(col.testId))
            : !alanTestIds || alanTestIds.includes(String(col.testId))));
        const outside = columns.filter((col) => !scoped.includes(col) && col.data.correct + col.data.incorrect > 0);
        return {
            scopedColumns: scoped,
            outsideColumns: outside,
            visibleColumns: columns.filter((col) => scoped.includes(col) || outside.includes(col)),
        };
    }, [columns, alan, isInternalYks, alanTestIds?.join(',')]);

    const totals = useMemo(() => {
        const correct = scopedColumns.reduce((sum, col) => sum + col.data.correct, 0);
        const incorrect = scopedColumns.reduce((sum, col) => sum + col.data.incorrect, 0);
        const empty = scopedColumns.reduce((sum, col) => sum + col.data.empty, 0);
        const totalQ = scopedColumns.reduce((sum, col) => sum + col.data.totalQ, 0);
        const net = correct - incorrect * netPenalty;
        const pct = totalQ > 0 ? (net / totalQ) * 100 : 0;
        return { correct, incorrect, empty, totalQ, net, pct };
    }, [scopedColumns, netPenalty]);
    // Özet kutusunun alt yazısı: hangi soruların sayıldığı (ör. "TYT + Sayısal · 200 soru")
    const scopeLabel = isInternalYks
        ? isSingleAyt
            ? `${alan ? YKS_ALAN_TESTS[alan].label : 'AYT'} · ${totals.totalQ} soru`
            : `${alan ? `TYT + ${YKS_ALAN_TESTS[alan].label}` : 'TYT'} · ${totals.totalQ} soru`
        : alanTestIds
            ? `${ALAN_PUANI_LABELS[alanPuani.alan] || alanPuani.alan} · ${totals.totalQ} soru`
            : `${totals.totalQ} soru`;

    const primaryScore = scoring?.primaryScore?.score;
    const scoreTypes = scoring?.scoreTypes || {};
    // Ana puan en yüksek AYT puanıdır; karnede öğrencinin alanının puanı gösterilir.
    const alanScore = alan && scoreTypes?.[alan]?.score != null ? scoreTypes[alan].score : null;
    const sessionScores = scoring?.sessionScores || {};
    const externalScoring = scoring?.externalScoring || {};
    const ogmResponseScores = externalScoring?.response?.scores || {};
    const tytRank = ogmResponseScores?.TYT?.averageRank;
    const aytScoreCards = [
        { key: 'SAY', label: 'Sayısal', value: scoreTypes?.SAY?.score, rank: ogmResponseScores?.SAY?.averageRank, range: ogmResponseScores?.SAY?.range },
        { key: 'EA', label: 'Eşit Ağırlık', value: scoreTypes?.EA?.score, rank: ogmResponseScores?.EA?.averageRank, range: ogmResponseScores?.EA?.range },
        { key: 'SOZ', label: 'Sözel', value: scoreTypes?.SOZ?.score, rank: ogmResponseScores?.SOZ?.averageRank, range: ogmResponseScores?.SOZ?.range },
    // YKS: yalnız öğrencinin alanının puan türü (üç AYT puanı birden kimin hangi puanla değerlendirildiğini bulandırıyordu)
    ].filter((item) => item.value != null && (!alan || item.key === alan));
    const rankingInfo =
        data?.additionalInfo?.sessionRankings?.general?.general ||
        data?.additionalInfo?.generalRanking ||
        stats?.scoring?.sessionRankings?.general?.general ||
        stats?.scoring?.primaryScore?.rankings?.general ||
        null;
    const rankingLabel =
        rankingInfo?.rank && rankingInfo?.participantCount
            ? `#${rankingInfo.rank} / ${rankingInfo.participantCount}`
            : null;

    const weakTopics = useMemo(() => {
        const map: Record<string, { subject: string; name: string; incorrect: number }> = {};
        filteredQuestions.forEach((q: any) => {
            if (q.achievement && q.isAnswered && !q.isCorrect) {
                const subject = q.subjectName || q.testName || 'Genel';
                const key = `${subject}|||${q.achievement}`;
                if (!map[key]) map[key] = { subject, name: q.achievement, incorrect: 0 };
                map[key].incorrect++;
            }
        });
        return Object.values(map).sort((a, b) => {
            const compare = a.subject.localeCompare(b.subject, 'tr');
            return compare !== 0 ? compare : b.incorrect - a.incorrect;
        });
    }, [filteredQuestions]);

    const weakBySubject = useMemo(() => {
        const groups: Record<string, typeof weakTopics> = {};
        weakTopics.forEach((topic) => {
            if (!groups[topic.subject]) groups[topic.subject] = [];
            groups[topic.subject].push(topic);
        });
        return groups;
    }, [weakTopics]);

    const detailedWeakBySubject = useMemo(() => {
        const groups: Record<string, Array<{ name: string; incorrect: number; questionNumber?: number }>> = {};
        filteredQuestions.forEach((q: any) => {
            if (!q.achievement || !q.isAnswered || q.isCorrect) return;
            const subject = q.subjectName || q.testName || 'Genel';
            if (!groups[subject]) groups[subject] = [];
            groups[subject].push({
                name: q.achievement,
                incorrect: 1,
                questionNumber: Number(q.questionNumber || 0) || undefined,
            });
        });
        return groups;
    }, [filteredQuestions]);

    const handleAnalyze = async () => {
        setIsAnalyzing(true);
        try {
            const payload = {
                examId: info?.examId || data?.additionalInfo?.examId || data?.examId,
                // Hangi öğrencinin sonucu: öğretmen/yönetici başkasının karnesinde analiz yaptığında da o sonuca kaydedilir
                resultId: data?.resultId,
                examName: examName || 'Deneme Sınavı',
                subjects: scopedColumns.map((col) => {
                    const subjectWeakTopics = detailedWeakBySubject[col.questions[0]?.subjectName || col.questions[0]?.testName || ''] ||
                        detailedWeakBySubject[col.fullHeader] ||
                        Object.entries(detailedWeakBySubject).filter(([key]) =>
                            key.toLowerCase().includes(normalizeLabel(col.fullHeader).split(' ')[0])
                        ).flatMap(([, value]) => value) ||
                        weakBySubject[col.questions[0]?.subjectName || col.questions[0]?.testName || ''] ||
                        weakBySubject[col.fullHeader] ||
                        Object.entries(weakBySubject).filter(([key]) =>
                            key.toLowerCase().includes(normalizeLabel(col.fullHeader).split(' ')[0])
                        ).flatMap(([, value]) => value) || [];

                    return {
                        name: col.fullHeader,
                        correct: col.data.correct,
                        incorrect: col.data.incorrect,
                        empty: col.data.empty,
                        net: col.data.net,
                        totalQ: col.data.totalQ,
                        weakTopics: subjectWeakTopics,
                    };
                }),
                totalNet: totals.net,
                totalQuestions: totals.totalQ,
                scorePercentage: totals.pct,
            };
            const result = await ApiService.getExamAnalysis(payload);
            setAiResult(result?.analysis);
            setAiSaveState(result?.persisted ? 'saved' : 'unsaved');
            setIsAiSectionOpen(true);
            if (result?.persisted && data?.resultId) {
                onAnalysisSaved?.(data.resultId, { content: result.analysis, generatedAt: new Date().toISOString() });
            }
        } catch (error) {
            console.error('AI Analiz Hatası', error);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const hasAiAnalysis = Boolean(aiResult);

    return (
        <div className="no-print space-y-6 pb-12">
            <div className="relative overflow-hidden rounded-[2rem] p-6 shadow-sm sm:p-8" style={{ border: '1px solid var(--theme-primary)' }}>
                <div className="pointer-events-none absolute inset-0 z-0" style={{ backgroundColor: 'var(--theme-primary)', opacity: 0.05 }} />
                <div className="pointer-events-none absolute right-0 top-0 h-full w-full opacity-[0.02] [mask-image:linear-gradient(to_bottom_left,black_0%,transparent_100%)] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPgo8cmVjdCB3aWR0aD0iOCIgaGVpZ2h0PSI4IiBmaWxsPSIjMDAwIiBmaWxsLW9wYWNpdHk9IjAuMSI+PC9yZWN0Pgo8cGF0aCBkPSJNMCAwdjhoOFYweiIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjMDAwIiBzdHJva2Utb3BhY2l0eT0iMC4xIi8+Cjwvc3ZnPg==')]" />

                <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-5">
                        {companyLogo && (
                            <div className="hidden h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-white p-2.5 shadow-sm sm:flex" style={{ border: '1px solid var(--theme-primary)' }}>
                                <img src={companyLogo} alt="" className="h-full w-full object-contain" />
                            </div>
                        )}
                        <div className="flex flex-col justify-center">
                            <span className="mb-1 text-[11px] font-bold uppercase tracking-[0.2em] opacity-70" style={{ color: 'var(--theme-primary)' }}>{companyName || 'Sınav Sonuç Denetimi'}</span>
                            <h3 className="text-2xl font-black tracking-tight text-slate-800 sm:text-3xl">{examName || 'Deneme Sınavı'}</h3>
                            <div className="mt-3 flex flex-wrap items-center gap-2 sm:gap-3">
                                <span className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm" style={{ border: '1px solid var(--theme-primary)' }}>
                                    <User size={14} style={{ color: 'var(--theme-primary)' }} /> {fullName}
                                </span>
                                {sube && (
                                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm" style={{ border: '1px solid var(--theme-primary)' }}>
                                        <GraduationCap size={14} style={{ color: 'var(--theme-primary)' }} /> {sube}
                                    </span>
                                )}
                                <span className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm" style={{ border: '1px solid var(--theme-primary)' }}>
                                    <FileText size={14} style={{ color: 'var(--theme-primary)' }} /> {bookletType || 'A'} Kitapçık
                                </span>
                                {rankingLabel && (
                                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm" style={{ border: '1px solid var(--theme-primary)' }}>
                                        <Award size={14} style={{ color: 'var(--theme-primary)' }} /> {rankingLabel}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {!disableAi && !hasAiAnalysis && (
                        <button
                            onClick={handleAnalyze}
                            disabled={isAnalyzing}
                            className="group relative inline-flex flex-shrink-0 items-center justify-center gap-2 overflow-hidden rounded-xl bg-theme-primary px-7 py-3.5 font-bold text-white shadow-[0_4px_15px_rgba(0,0,0,0.1)] transition-all hover:-translate-y-0.5 hover:bg-theme-secondary hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70 disabled:transform-none"
                        >
                            <div className="pointer-events-none absolute inset-0 h-full w-full bg-white/20 [transform:skewX(-20deg)_translateX(-150%)] transition-transform duration-700 group-hover:[transform:skewX(-20deg)_translateX(150%)]" />
                            {isAnalyzing ? <Loader2 size={18} className="animate-spin text-white" /> : <Sparkles size={18} className="text-white drop-shadow-sm" />}
                            <span className="tracking-wide">AI ile Analiz Et</span>
                        </button>
                    )}
                </div>
            </div>

            <div className={`relative overflow-hidden rounded-3xl border border-gray-100 bg-white p-1 shadow-xl ${disableAi ? 'hidden' : ''}`}>
                <div className="pointer-events-none absolute right-0 top-0 h-64 w-64 rounded-bl-full bg-theme-primary opacity-5" />

                <div className="rounded-[1.4rem] bg-white p-6 sm:p-8">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-theme-primary text-white shadow-md">
                                <Sparkles size={20} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-theme-primary">Yapay Zeka Analizi</h3>
                                <p className="text-sm font-medium text-gray-500">
                                    Sonuçlarına göre kişiselleştirilmiş strateji
                                    {aiSaveState === 'saved' && <span className="ml-2 text-xs font-semibold text-emerald-600">· Karneye kaydedildi</span>}
                                    {aiSaveState === 'unsaved' && <span className="ml-2 text-xs font-semibold text-amber-600">· Kaydedilemedi, bu ekranda geçici</span>}
                                </p>
                            </div>
                        </div>

                        {hasAiAnalysis ? (
                            <button
                                type="button"
                                onClick={() => setIsAiSectionOpen((prev) => !prev)}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-theme-primary"
                                aria-label={isAiSectionOpen ? 'Analizi kapat' : 'Analizi aç'}
                            >
                                <ChevronDown size={20} className={`transition-transform duration-200 ${isAiSectionOpen ? 'rotate-180' : ''}`} />
                            </button>
                        ) : (
                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-400">
                                <X size={18} />
                            </div>
                        )}
                    </div>

                    {!hasAiAnalysis && (
                        <div className="mt-5 rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-500">
                            Henüz kayıtlı bir yapay zeka analizi yok.
                        </div>
                    )}

                    {hasAiAnalysis && isAiSectionOpen && (
                        <div className="animate-in fade-in slide-in-from-top-2 mt-6 duration-300">
                            {aiResult.overallAssessment && (
                                <div className="relative mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 p-5 text-gray-800 shadow-sm">
                                    <div className="absolute left-0 top-0 h-full w-1 bg-theme-primary" />
                                    <p className="relative z-10 text-[15px] font-medium leading-relaxed">{cleanAiText(aiResult.overallAssessment)}</p>
                                </div>
                            )}

                            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                                {Array.isArray(aiResult.prioritySubjects) && aiResult.prioritySubjects.map((ps: any, idx: number) => (
                                    <div key={idx} className="flex flex-col rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md md:min-h-[420px]">
                                        <div className="mb-3 flex items-center justify-between">
                                            <h4 className="flex items-center gap-2 text-base font-bold text-gray-800">
                                                <AlertCircle size={16} className={ps.urgency === 'high' ? 'text-red-500' : ps.urgency === 'medium' ? 'text-amber-500' : 'text-blue-500'} />
                                                {ps.subject}
                                            </h4>
                                            <span className={`rounded-md px-2.5 py-1 text-[10px] font-bold uppercase ${ps.urgency === 'high'
                                                ? 'border border-red-100 bg-red-50 text-red-600'
                                                : ps.urgency === 'medium'
                                                    ? 'border border-amber-100 bg-amber-50 text-amber-600'
                                                    : 'border border-blue-100 bg-blue-50 text-blue-600'
                                                }`}>
                                                {ps.urgency === 'high' ? 'Yüksek Öncelik' : ps.urgency === 'medium' ? 'Orta Öncelik' : 'Düşük Öncelik'}
                                            </span>
                                        </div>
                                        <p className="mb-4 text-xs text-gray-500">{ps.summary}</p>

                                        <div className="space-y-3">
                                            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
                                                <Notebook size={14} className="text-gray-400" /> Eksik Konular
                                            </div>
                                            {Array.isArray(ps.topics) && ps.topics.map((topic: any, index: number) => (
                                                <div key={index} className="relative border-l-2 border-indigo-100 pl-4">
                                                    <div className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-indigo-300" />
                                                    <div className="text-[13px] font-bold text-gray-800">{topic.name} <span className="ml-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-500">{topic.wrongCount} Y</span></div>
                                                    <div className="mt-0.5 text-[12px] text-gray-600">{topic.advice}</div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="-mx-5 -mb-5 mt-auto rounded-b-2xl border-t border-gray-100 bg-gray-50/50 px-5 pb-5 pt-4">
                                            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
                                                <Lightbulb size={14} className="text-amber-500" /> Çalışma Stratejisi
                                            </div>
                                            <p className="text-[13px] text-gray-600">{cleanAiText(ps.studyPlan)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                {aiResult.weeklyPlan && (
                                    <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-5 shadow-sm">
                                        <h4 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-blue-900">
                                            <TrendingUp size={16} className="text-blue-600" /> Haftalık Plan
                                        </h4>
                                        <p className="text-[13px] leading-relaxed text-blue-800">{cleanAiText(aiResult.weeklyPlan)}</p>
                                    </div>
                                )}

                                {aiResult.motivationalNote && (
                                    <div className="flex flex-col justify-center rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-fuchsia-50 p-5 text-center shadow-sm">
                                        <Award size={32} className="mx-auto mb-3 text-purple-400 opacity-50" />
                                        <p className="text-[14px] font-medium italic text-purple-900">{cleanAiText(aiResult.motivationalNote)}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
                <StatCard label="Toplam Net" value={totals.net.toFixed(2)} sub={scopeLabel} color="blue" large />
                <StatCard label="Başarı Oranı" value={`%${totals.pct.toFixed(0)}`} sub={scopeLabel} color={totals.pct >= 50 ? 'green' : 'red'} large />
                <StatCard label="Doğru" value={String(totals.correct)} sub="cevap" color="green" />
                <StatCard label="Yanlış" value={String(totals.incorrect)} sub="cevap" color="red" />
                {alanPuani ? (
                    <StatCard label="Puan" value={Number(alanPuani.score).toFixed(2)} sub={ALAN_PUANI_LABELS[alanPuani.alan] || alanPuani.alan} color="purple" large />
                ) : alanScore != null ? (
                    <StatCard label="Puan" value={Number(alanScore).toFixed(2)} sub={`AYT ${alan ? YKS_ALAN_TESTS[alan].label : ''}`} color="purple" large />
                ) : primaryScore != null && (
                    <StatCard label="Puan" value={Number(primaryScore).toFixed(2)} sub={aytScoreCards.length > 0 ? 'AYT' : (sessionScores?.labels?.total || 'Genel')} color="purple" large />
                )}
            </div>

            {alanPuani && (
                <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-indigo-500">Alan Sırası ({ALAN_PUANI_LABELS[alanPuani.alan] || alanPuani.alan})</p>
                            <p className="mt-1 text-xl font-bold text-indigo-700">
                                {alanPuani.ranks?.general ? `${alanPuani.ranks.general.rank} / ${alanPuani.ranks.general.participantCount}` : '-'}
                            </p>
                            {alanPuani.ranks?.general && <p className="mt-1 text-[11px] text-indigo-700">ilk %{Number(alanPuani.ranks.general.topPercent).toLocaleString('tr-TR')}</p>}
                        </div>
                        {['SAY', 'EA', 'SOZ'].filter((type) => alanPuani.scores?.[type] != null).map((type) => (
                            <div key={type} className={`rounded-xl border p-4 ${type === alanPuani.alan ? 'border-fuchsia-100 bg-fuchsia-50' : 'border-gray-100 bg-gray-50'}`}>
                                <p className={`text-xs font-medium uppercase tracking-wide ${type === alanPuani.alan ? 'text-fuchsia-500' : 'text-gray-500'}`}>
                                    {ALAN_PUANI_LABELS[type]}{type === alanPuani.alan ? ' (alan)' : ''}
                                </p>
                                <p className={`mt-1 text-xl font-bold ${type === alanPuani.alan ? 'text-fuchsia-700' : 'text-gray-700'}`}>{Number(alanPuani.scores[type]).toFixed(2)}</p>
                            </div>
                        ))}
                    </div>
                    <p className="text-[11px] text-gray-500">
                        Alan puanı: alanın iki testi 40 soruya tamamlanır, soru başına 5 puan; 80 soru × 5 = 400 puana 100 taban eklenir (en çok 500). Eksi net 0 sayılır. Sıra bu denemeye giren aynı alandaki öğrenciler içindir.
                    </p>
                </div>
            )}

            {sessionScores?.labels?.primary && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {sessionScores.labels.primary && !isSingleAyt && (
                        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-indigo-500">{sessionScores.labels.primary}</p>
                            <p className="mt-1 text-xl font-bold text-indigo-700">
                                {Number(sessionScores.tytScore ?? sessionScores.session1Score ?? 0).toFixed(2)}
                            </p>
                            {tytRank && (
                                <p className="mt-1 text-[11px] text-indigo-700">
                                    Türkiye sıra: {ogmResponseScores?.TYT?.range || '-'} · Ort: {formatRank(tytRank)}
                                </p>
                            )}
                        </div>
                    )}
                    {aytScoreCards.length > 0 ? aytScoreCards.map((item) => (
                        <div key={item.key} className="rounded-xl border border-fuchsia-100 bg-fuchsia-50 p-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-fuchsia-500">AYT - {item.label}</p>
                            <p className="mt-1 text-xl font-bold text-fuchsia-700">
                                {Number(item.value || 0).toFixed(2)}
                            </p>
                            {item.rank && (
                                <p className="mt-1 text-[11px] text-fuchsia-700">
                                    Türkiye sıra: {item.range || '-'} · Ort: {formatRank(item.rank)}
                                </p>
                            )}
                        </div>
                    )) : sessionScores.labels.secondary && (
                        <div className="rounded-xl border border-fuchsia-100 bg-fuchsia-50 p-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-fuchsia-500">{sessionScores.labels.secondary}</p>
                            <p className="mt-1 text-xl font-bold text-fuchsia-700">
                                {Number(sessionScores.aytScore ?? sessionScores.session2Score ?? 0).toFixed(2)}
                            </p>
                        </div>
                    )}
                    {aytScoreCards.length === 0 && sessionScores.labels.total && sessionScores.labels.total !== sessionScores.labels.primary && (
                        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-blue-500">{sessionScores.labels.total}</p>
                            <p className="mt-1 text-xl font-bold text-blue-700">
                                {Number((sessionScores.totalScore ?? sessionScores.generalScore) || 0).toFixed(2)}
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div>
                <h4 className="mb-3 text-base font-semibold text-gray-800">Test Performansı</h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {visibleColumns.map((col, idx) => {
                        const pct = col.data.totalQ > 0 ? (col.data.net / col.data.totalQ) * 100 : 0;
                        const colors = getSubjectColor(col.fullHeader);
                        const netDiff = col.data.net - col.data.avgNet;
                        const outside = outsideColumns.includes(col);
                        return (
                            <div key={idx} className={`rounded-xl border border-gray-200/60 p-4 transition-shadow hover:shadow-md ${colors.bg} ${outside ? 'opacity-60' : ''}`}>
                                <div className="mb-2.5 flex items-center justify-between">
                                    <span className={`text-sm font-bold ${colors.text}`}>
                                        {col.header}
                                        {outside && <span className="ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">alan dışı</span>}
                                    </span>
                                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${colors.light} ${colors.text}`}>
                                        {col.data.net.toFixed(2)} net
                                    </span>
                                </div>
                                <div className="relative mb-3 h-2.5 overflow-hidden rounded-full bg-gray-200/70">
                                    <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${colors.bar}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-600">
                                    <div className="flex items-center gap-2.5">
                                        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-500" />{col.data.correct}</span>
                                        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" />{col.data.incorrect}</span>
                                        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-gray-400" />{col.data.empty}</span>
                                    </div>
                                    <span className="font-medium">%{pct.toFixed(0)}</span>
                                </div>
                                {col.data.avgNet > 0 && !outside && (
                                    <div className="mt-2 flex items-center justify-between border-t border-gray-200/60 pt-2 text-[11px] text-gray-500">
                                        <span>Sınav Ort: {col.data.avgNet.toFixed(2)}</span>
                                        <span className={`font-semibold ${netDiff >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                            {netDiff >= 0 ? '▲' : '▼'} {Math.abs(netDiff).toFixed(2)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div>
                <h4 className="mb-3 text-base font-semibold text-gray-800">Detaylı Analiz</h4>
                <div className="space-y-4">
                    {visibleColumns.map((col, idx) => {
                        if (!col.questions.length) return null;
                        const colors = getSubjectColor(col.fullHeader);
                        const pct = col.data.totalQ > 0 ? (col.data.net / col.data.totalQ) * 100 : 0;
                        const subjectWeakTopics = weakBySubject[col.questions[0]?.subjectName || col.questions[0]?.testName || ''] ||
                            weakBySubject[col.fullHeader] ||
                            Object.entries(weakBySubject).filter(([key]) =>
                                key.toLowerCase().includes(normalizeLabel(col.fullHeader).split(' ')[0])
                            ).flatMap(([, value]) => value) || [];

                        return (
                            <div key={idx} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
                                <div className={`flex items-center justify-between border-b border-gray-200/60 px-5 py-3 ${colors.bg}`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${colors.light}`}>
                                            <span className={`text-sm font-bold ${colors.text}`}>{idx + 1}</span>
                                        </div>
                                        <div>
                                            <span className={`text-sm font-bold ${colors.text}`}>{col.header}</span>
                                            <div className="mt-0.5 flex items-center gap-2">
                                                <span className="text-[10px] text-gray-500">{col.data.totalQ} soru</span>
                                                <span className="text-[10px] text-gray-300">|</span>
                                                <span className="text-[10px] font-semibold text-green-600">{col.data.correct}D</span>
                                                <span className="text-[10px] font-semibold text-red-500">{col.data.incorrect}Y</span>
                                                <span className="text-[10px] font-semibold text-gray-400">{col.data.empty}B</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`text-lg font-bold ${colors.text}`}>{col.data.net.toFixed(2)}</div>
                                        <div className="text-[10px] text-gray-500">%{pct.toFixed(0)} başarı</div>
                                    </div>
                                </div>

                                <div className="border-b border-gray-100 px-5 py-3">
                                    <div className="mb-2 flex items-center gap-2">
                                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Cevap Anahtarı</span>
                                        <div className="h-px flex-1 bg-gray-100" />
                                        <div className="flex items-center gap-3 text-[10px] text-gray-400">
                                            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-green-200" /> Doğru</span>
                                            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-red-200" /> Yanlış</span>
                                            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-gray-200" /> Boş</span>
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto pb-1">
                                        <div className="flex min-w-max gap-[2px] lg:gap-1">
                                            {col.questions.map((q: any, questionIndex: number) => {
                                                const correct = q.isCorrect;
                                                const wrong = q.isAnswered && !q.isCorrect;
                                                return (
                                                    <div
                                                        key={questionIndex}
                                                        className="flex flex-col items-center"
                                                        style={{ width: 'clamp(24px, 1.8vw, 36px)', minWidth: 'clamp(24px, 1.8vw, 36px)' }}
                                                    >
                                                        <span className="mb-0.5 text-[8px] font-medium text-gray-400 lg:text-[10px]">{questionIndex + 1}</span>
                                                        <div className={`flex h-[22px] w-[22px] items-center justify-center rounded-md border text-[10px] font-bold lg:h-[30px] lg:w-[30px] lg:text-xs 2xl:h-[34px] 2xl:w-[34px] ${correct
                                                            ? 'border-green-200 bg-green-50 text-green-700'
                                                            : wrong
                                                                ? 'border-red-200 bg-red-50 text-red-600'
                                                                : 'border-gray-200 bg-gray-50 text-gray-400'
                                                            }`}>
                                                            {q.studentAnswer || '–'}
                                                        </div>
                                                        <span className={`mt-0.5 text-[8px] font-medium lg:text-[10px] ${correct ? 'text-green-600' : wrong ? 'text-gray-400 line-through' : 'text-gray-300'}`}>
                                                            {q.correctAnswer || '-'}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {subjectWeakTopics.length > 0 && (
                                    <div className="bg-gray-50/50 px-5 py-3">
                                        <div className="mb-2 flex items-center gap-2">
                                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Eksik Konular</span>
                                            <div className="h-px flex-1 bg-gray-200/60" />
                                            <span className="text-[10px] text-gray-400">{subjectWeakTopics.length} konu</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {subjectWeakTopics.map((topic: any, index: number) => (
                                                <span key={index} className="inline-flex items-center gap-1 rounded-md border border-red-100 bg-white px-2 py-1 text-[11px] text-red-700 shadow-sm">
                                                    <span className="h-1 w-1 flex-shrink-0 rounded-full bg-red-400" />
                                                    <span className="max-w-[200px] truncate">{topic.name}</span>
                                                    <span className="ml-0.5 font-bold text-red-500">{topic.incorrect}</span>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {totalStudents > 0 && (
                <div className="pt-2 text-center text-xs text-gray-400">
                    Bu sınava toplam <span className="font-semibold text-gray-500">{totalStudents}</span> öğrenci katılmıştır.
                </div>
            )}
        </div>
    );
}
